import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import * as React from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSessionQuery } from "@/hooks/queries/useSessionQuery";
import { captureError } from "@/lib/error-tracking";
import { logger } from "@/lib/logger";
import { coordinateProfileCreation } from "@/lib/profile-creation-coordinator";

export interface UserProfile {
  full_name: string | null;
  company_name: string | null;
  email: string | null;
  avatar_url: string | null;
  company_logo_url?: string | null; // May not exist in all databases
  logo_url?: string | null; // Alternative column name
  display_preference?: 'name' | 'company' | null; // User preference: name (First L) or company
}

/**
 * Centralized hook for user profile data
 * - Uses single query key: ['profile', userId]
 * - Fetches all profile fields
 * - Automatically listens for profile updates
 * - Single source of truth for profile data
 */
export function useUserProfile() {
  const queryClient = useQueryClient();
  const { data: session } = useSessionQuery();
  const userId = session?.user?.id;

  // Single query key pattern - all components use this
  const { data: profile, isLoading, error } = useQuery<UserProfile | null>({
    queryKey: ['profile', userId],
    queryFn: async () => {
      if (!userId) {
        logger.warn('useUserProfile: No userId provided');
        return null;
      }

      logger.debug(`useUserProfile: Fetching profile for user ${userId}`);

      // Try fetching profile with better error handling
      // Use .maybeSingle() to safely handle missing profiles without throwing
      // First try with company_logo_url, fallback to logo_url if column doesn't exist
      let { data, error: queryError } = await supabase
        .from("profiles")
        .select("full_name, company_name, email, avatar_url, company_logo_url, display_preference")
        .eq("id", userId)
        .maybeSingle();
      
      // If company_logo_url or display_preference column doesn't exist (error 42703), retry with minimal columns
      if (queryError?.code === '42703') {
        logger.warn('One or more profile columns not found, retrying with minimal set');
        const retry = await supabase
          .from("profiles")
          .select("full_name, company_name, email, avatar_url, logo_url")
          .eq("id", userId)
          .maybeSingle();
        
        if (!retry.error && retry.data) {
          // Map logo_url to company_logo_url for consistency in the app
          data = {
            ...retry.data,
            company_logo_url: retry.data.logo_url || null
          };
          queryError = null; // Clear error since retry succeeded
        } else {
          // If logo_url also fails, try without any logo column
          const finalRetry = await supabase
            .from("profiles")
            .select("full_name, company_name, email, avatar_url")
            .eq("id", userId)
            .maybeSingle();
          data = finalRetry.data;
          queryError = finalRetry.error;
        }
      }

      if (queryError) {
        // Log detailed error information (only in dev for debugging)
        if (import.meta.env.DEV) {
          logger.error("Error fetching profile", {
            code: queryError.code,
            message: queryError.message,
            details: queryError.details,
            hint: queryError.hint,
            userId
          });
        }

        // Capture error for tracking
        captureError(new Error(`Profile fetch failed: ${queryError.message}`), {
          component: 'useUserProfile',
          action: 'fetchProfile',
          userId,
          additionalData: {
            errorCode: queryError.code,
            errorDetails: queryError.details,
            errorHint: queryError.hint,
          }
        });

        // 400 error = bad request (RLS blocking or column issue)
        if (queryError.code === 'PGRST301' || queryError.message?.includes('400') || queryError.code?.startsWith('42')) {
          if (import.meta.env.DEV) {
            logger.error('RLS policy issue or column doesn\'t exist', {
              errorCode: queryError.code,
              errorMessage: queryError.message,
              errorDetails: queryError.details,
              errorHint: queryError.hint,
              troubleshooting: [
                'Check RLS policies allow SELECT on profiles table',
                'Verify columns exist: full_name, company_name, email, avatar_url, company_logo_url',
                'Check if user has admin role in user_roles table',
                'Test query directly in Supabase SQL Editor'
              ]
            });
          }
        }

        // Don't throw - return null so component can show fallback
        return null;
      }

      // Log profile status for debugging
      if (!data) {
        logger.warn(`Profile not found for user ${userId}. Profile may need to be created.`);
      } else {
        // CRITICAL: Log full_name status in detail
        logger.debug(`Profile loaded for user ${userId}`, {
          full_name: data.full_name,
          full_name_type: typeof data.full_name,
          full_name_length: data.full_name?.length,
          full_name_trimmed: data.full_name?.trim(),
          company_name: data.company_name,
          email: data.email,
          allKeys: Object.keys(data)
        });
        
        if (!data.full_name && !data.company_name) {
          logger.warn(`Profile exists but has no name fields for user ${userId}`, {
            email: data.email,
            full_name: data.full_name,
            company_name: data.company_name
          });
        }
      }

      return data;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true, // Changed to true to ensure fresh data
    retry: 2, // Retry failed queries twice
    retryDelay: 1000, // Wait 1 second between retries
  });

  // Listen for profile updates and invalidate cache
  useEffect(() => {
    const handleProfileUpdate = (event: CustomEvent) => {
      const { userId: updatedUserId } = event.detail;
      
      logger.debug('Profile update event received', { updatedUserId, currentUserId: userId });
      
      // Invalidate profile query for the updated user
      queryClient.invalidateQueries({ queryKey: ['profile', updatedUserId] });
      
      // If this update is for the current user, refetch immediately
      if (updatedUserId === userId) {
        logger.debug('Refetching profile for current user');
        queryClient.refetchQueries({ queryKey: ['profile', userId] });
      }
    };

    window.addEventListener('profile-updated', handleProfileUpdate as EventListener);
    return () => {
      window.removeEventListener('profile-updated', handleProfileUpdate as EventListener);
    };
  }, [userId, queryClient]);

  // Auto-create profile if missing (only in development or with explicit flag)
  // Use global coordinator to prevent multiple simultaneous creation attempts
  const retryCountRef = React.useRef(0);
  const MAX_RETRIES = 3;
  const RETRY_DELAY_BASE = 1000; // 1 second base delay
  
  useEffect(() => {
    // Only auto-create if profile is null, not loading, and user is authenticated
    if (!isLoading && !profile && userId && session?.user?.email) {
      const shouldAutoCreate = import.meta.env.DEV || localStorage.getItem('holescale_auto_create_profile') === 'true';
      
      if (shouldAutoCreate && retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current += 1;
        
        // Exponential backoff delay
        const delay = Math.min(RETRY_DELAY_BASE * Math.pow(2, retryCountRef.current - 1), 10000);
        
        // Use global coordinator to prevent multiple simultaneous attempts
        coordinateProfileCreation(userId, async () => {
          // Wait before retrying (except first attempt)
          if (retryCountRef.current > 1) {
            await new Promise(resolve => setTimeout(resolve, delay));
          }
          
          logger.debug('Auto-creating profile for user', { userId, attempt: retryCountRef.current });
          
          // CRITICAL: Check if profile exists before creating
          // Don't use upsert as it would overwrite existing user-saved data
          const { data: existingProfile } = await supabase
            .from('profiles')
            .select('id, full_name')
            .eq('id', userId)
            .maybeSingle();
          
          // Only create if profile doesn't exist
          if (existingProfile) {
            logger.debug('Profile already exists, skipping auto-create', { 
              userId, 
              hasFullName: !!existingProfile.full_name 
            });
            retryCountRef.current = 0; // Reset retry count
            return;
          }
          
          // Extract name from user metadata or email (only for new profiles)
          const fullName = session.user.user_metadata?.full_name || 
                          session.user.user_metadata?.name ||
                          session.user.email.split('@')[0];
          
          try {
            const { error } = await supabase
              .from('profiles')
              .insert({
                id: userId,
                email: session.user.email,
                full_name: fullName,
              });
            
            if (error) {
              // If RLS error and haven't exceeded retries, don't log as error yet
              if (error.code === '42501' && retryCountRef.current < MAX_RETRIES) {
                logger.warn('Profile upsert failed (RLS), will retry', {
                  attempt: retryCountRef.current,
                  maxRetries: MAX_RETRIES,
                  code: error.code,
                  message: error.message
                });
                return;
              }
              
              logger.error('Failed to upsert profile', {
                code: error.code,
                message: error.message,
                details: error.details,
                hint: error.hint,
                attempt: retryCountRef.current
              });
              
              // Reset retry count on permanent failure
              if (error.code !== '42501') {
                retryCountRef.current = 0;
              }
            } else {
              logger.debug('Profile upserted successfully', { attempt: retryCountRef.current });
              retryCountRef.current = 0; // Reset on success
              // Invalidate query to refetch
              queryClient.invalidateQueries({ queryKey: ['profile', userId] });
            }
          } catch (err) {
            logger.error('Exception during auto-create', { 
              error: err,
              attempt: retryCountRef.current
            });
          }
        }).catch(err => {
          logger.error('Profile creation coordination failed', { error: err, userId });
        });
      } else if (retryCountRef.current >= MAX_RETRIES) {
        logger.error('Max retries reached for profile creation', { 
          userId,
          maxRetries: MAX_RETRIES 
        });
        // Reset after a longer delay to allow manual intervention
        setTimeout(() => {
          retryCountRef.current = 0;
        }, 60000); // Reset after 1 minute
      }
    }
  }, [isLoading, profile, userId, session, queryClient]);

  return {
    profile,
    isLoading,
    error,
    userId,
  };
}

